'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Symptom {
  id: string;
  name: string;
  severity: 'critical' | 'urgent' | 'monitor';
}

interface SymptomCheckerProps {
  symptoms: Symptom[];
}

const SymptomChecker: React.FC<SymptomCheckerProps> = ({ symptoms }) => {
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);

  const toggleSymptom = (symptomId: string) => {
    setSelectedSymptoms(prev =>
      prev.includes(symptomId)
        ? prev.filter(id => id !== symptomId)
        : [...prev, symptomId]
    );
    setShowResults(false);
  };

  const analyzeSymptoms = () => {
    setShowResults(true);
  };

  const getRecommendation = () => {
    const selected = symptoms.filter(s => selectedSymptoms.includes(s.id));
    const hasCritical = selected.some(s => s.severity === 'critical');
    const hasUrgent = selected.some(s => s.severity === 'urgent');

    if (hasCritical) {
      return {
        level: 'critical',
        title: 'Seek Emergency Care Immediately',
        message: 'Your pet is showing critical symptoms that require immediate veterinary attention. Do not wait - contact an emergency vet or animal hospital right now.',
        action: 'Find Emergency Vet',
        icon: 'ExclamationTriangleIcon',
        bgColor: 'bg-error/10',
        borderColor: 'border-error',
        textColor: 'text-error'
      };
    } else if (hasUrgent) {
      return {
        level: 'urgent',
        title: 'Contact Your Veterinarian Today',
        message: 'Your pet is showing symptoms that need prompt attention. Contact your veterinarian within the next few hours to schedule an appointment.',
        action: 'View Care Guidelines',
        icon: 'ClockIcon',
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning',
        textColor: 'text-warning'
      };
    } else {
      return {
        level: 'monitor',
        title: 'Monitor and Observe',
        message: 'Continue monitoring your pet\'s condition. If symptoms worsen or new symptoms appear, contact your veterinarian.',
        action: 'Learn More',
        icon: 'EyeIcon',
        bgColor: 'bg-primary/10',
        borderColor: 'border-primary',
        textColor: 'text-primary'
      };
    }
  };

  const recommendation = showResults ? getRecommendation() : null;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-2xl font-headline font-bold text-foreground mb-2 flex items-center space-x-2">
          <Icon name="ClipboardDocumentListIcon" size={28} variant="solid" className="text-primary" />
          <span>Emergency Symptom Checker</span>
        </h3>
        <p className="text-muted-foreground">
          Select all symptoms your pet is experiencing to receive immediate guidance
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {symptoms.map((symptom) => {
          const isSelected = selectedSymptoms.includes(symptom.id);
          const severityColors = {
            critical: 'border-error hover:bg-error/5',
            urgent: 'border-warning hover:bg-warning/5',
            monitor: 'border-primary hover:bg-primary/5'
          };

          return (
            <button
              key={symptom.id}
              onClick={() => toggleSymptom(symptom.id)}
              className={`text-left p-4 border-2 rounded-lg transition-all duration-300 ${
                isSelected
                  ? 'bg-primary text-white border-primary shadow-md'
                  : `bg-background ${severityColors[symptom.severity]}`
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{symptom.name}</span>
                {isSelected && (
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-white" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={analyzeSymptoms}
        disabled={selectedSymptoms.length === 0}
        className="w-full py-4 bg-primary text-white font-headline font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
      >
        <Icon name="MagnifyingGlassIcon" size={20} variant="solid" />
        <span>Analyze Symptoms</span>
      </button>

      {showResults && recommendation && (
        <div className={`mt-6 border-2 rounded-lg p-6 ${recommendation.bgColor} ${recommendation.borderColor}`}>
          <div className="flex items-start space-x-4">
            <Icon 
              name={recommendation.icon as any} 
              size={32} 
              variant="solid" 
              className={recommendation.textColor}
            />
            <div className="flex-1">
              <h4 className={`text-xl font-headline font-bold mb-2 ${recommendation.textColor}`}>
                {recommendation.title}
              </h4>
              <p className="text-foreground mb-4">{recommendation.message}</p>
              <button className={`px-6 py-2 ${recommendation.textColor} border-2 ${recommendation.borderColor} font-semibold rounded-lg hover:bg-white/50 transition-colors`}>
                {recommendation.action}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SymptomChecker;